/**
 * Online GIF provider policy — SPEC §1.2 (minimal metadata), §19, NFR-PWA-002.
 *
 * Design constraints:
 * - Consent-gated: GIF search is OFF until the user explicitly enables it.
 * - Server-side proxy only: the client never contacts the provider directly.
 *   Search JSON and GIF bytes flow through same-origin API routes
 *   (`/api/gif/search`, `/api/gif/media`), so:
 *     · the provider API key never ships in the client bundle,
 *     · the provider never learns user IPs,
 *     · CSP `img-src 'self' data: blob:` still holds (no third-party hosts).
 * - Strict media-host allowlist: the proxy fetches only from approved CDN hosts.
 * - Sent GIFs travel through the E2EE attachment path (SEC-MED-*), so the
 *   provider and proxy never learn recipients or ciphertext routing.
 */

export interface GifProviderDescriptor {
  id: string;
  label: string;
  /** CDN hosts the media proxy may fetch from (https only). */
  mediaHosts: readonly string[];
  /** Provider API base used server-side only. */
  apiBase: string;
  attribution: string;
}

export const TENOR_PROVIDER: GifProviderDescriptor = {
  id: "tenor",
  label: "Tenor (Google)",
  mediaHosts: ["media.tenor.com", "c.tenor.com", "media1.tenor.com", "media2.tenor.com", "media3.tenor.com"],
  apiBase: "https://tenor.googleapis.com/v2",
  attribution: "GIFs by Tenor (tenor.googleapis.com). Search terms are shared with Tenor via the Veto proxy.",
};

export const GIF_PROVIDERS: readonly GifProviderDescriptor[] = [TENOR_PROVIDER];

/** Max GIF bytes the media proxy will fetch/relay (alpha cap; SPEC resource budgets). */
export const MAX_GIF_PROXY_BYTES = 10 * 1024 * 1024;

/** Parse a strict https URL and return hostname, or null when malformed. */
export function safeUrlHost(raw: string): string | null {
  try {
    const u = new URL(raw);
    if (u.protocol !== "https:") return null;
    return u.hostname.toLowerCase();
  } catch {
    return null;
  }
}

/** Only approved provider CDN hosts may be fetched by the media proxy. */
export function isAllowedGifMediaUrl(raw: string, providers: readonly GifProviderDescriptor[] = GIF_PROVIDERS): boolean {
  const host = safeUrlHost(raw);
  if (!host) return false;
  return providers.some((p) => p.mediaHosts.includes(host));
}

/** Validate a Tenor v2 media format URL shape (extra defense in the proxy). */
export function isTenorMediaUrl(raw: string): boolean {
  return isAllowedGifMediaUrl(raw, [TENOR_PROVIDER]);
}

export const GIF_FEATURE_STATUS = {
  /** Search stays consent-gated; local GIF import needs no consent. */
  localImportAvailableInUi: true,
  onlineSearchAvailableInUi: "consent-gated",
  providers: GIF_PROVIDERS.map((p) => p.id),
  proxy: "same-origin",
} as const;
